import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { GarageDetailResponse } from '../types';

interface ReviewResponse {
  id: string;
  orderId: string;
  customerId: string;
  garageId: string;
  serviceName: string;
  carLabel: string;
  rating: number;
  comment: string;
  photoUrls: string[];
  garageResponse: string | null;
  garageResponseAt: string | null;
  status: string;
  createdAt: string;
}

interface RatingSummary {
  garageId: string;
  averageRating: number;
  reviewCount: number;
}

interface GarageReviewsResponse {
  ratingSummary: RatingSummary;
  reviews: {
    content: ReviewResponse[];
    totalPages: number;
    number: number;
    totalElements: number;
  };
}

function Stars({ rating, size = 'md' }: { rating: number; size?: 'sm' | 'md' | 'lg' }) {
  const fontSize = size === 'lg' ? '1.25rem' : size === 'sm' ? '0.75rem' : '1rem';
  return (
    <span style={{ color: '#f59e0b', fontSize }}>
      {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
    </span>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  return `${Math.floor(months / 12)} year${Math.floor(months / 12) > 1 ? 's' : ''} ago`;
}

export default function ReviewsPage() {
  const auth = useAuth();
  const [garage, setGarage] = useState<GarageDetailResponse | null>(null);
  const [summary, setSummary] = useState<RatingSummary | null>(null);
  const [reviews, setReviews] = useState<ReviewResponse[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!auth.token) return;
    api.getMyGarage(auth.token)
      .then(g => setGarage(g))
      .catch(() => setError('Failed to load garage'));
  }, [auth.token]);

  const fetchReviews = useCallback(async () => {
    if (!garage || !auth.token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<GarageReviewsResponse>(
        `/api/garages/${garage.id}/reviews?page=${page}&size=10`,
        auth.token,
      );
      setSummary(data.ratingSummary);
      setReviews(data.reviews.content ?? []);
      setTotalPages(data.reviews.totalPages ?? 0);
      setTotalElements(data.reviews.totalElements ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [garage, auth.token, page]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const submitReply = async (reviewId: string) => {
    if (!auth.token || !replyText.trim()) return;
    setSubmitting(true);
    try {
      await api.put(`/api/reviews/${reviewId}/respond`, { response: replyText.trim() }, auth.token);
      setReplyingTo(null);
      setReplyText('');
      await fetchReviews();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to submit reply');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !garage) {
    return <p style={{ color: '#6b7280' }}>Loading...</p>;
  }

  return (
    <div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '1.5rem' }}>Reviews</h1>

      {summary && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '1.5rem',
          background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px',
          padding: '1.25rem 1.5rem', marginBottom: '1.5rem',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#1a1a2e', lineHeight: 1 }}>
              {summary.averageRating.toFixed(1)}
            </div>
            <Stars rating={Math.round(summary.averageRating)} size="lg" />
          </div>
          <div style={{ width: '1px', height: '48px', background: '#e5e7eb' }} />
          <div>
            <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1a1a2e' }}>{summary.reviewCount}</div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total reviews</div>
          </div>
        </div>
      )}

      {error && <p style={{ color: '#dc2626' }}>{error}</p>}

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading reviews...</p>
      ) : reviews.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '3rem 1rem',
          border: '1px dashed #d1d5db', borderRadius: '10px', color: '#6b7280',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⭐</div>
          <p style={{ fontWeight: 500, margin: '0 0 0.25rem' }}>No reviews yet</p>
          <p style={{ fontSize: '0.875rem', margin: 0 }}>Reviews from completed orders will appear here.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {reviews.map(review => (
            <div
              key={review.id}
              style={{
                background: 'white', border: '1px solid #e5e7eb',
                borderRadius: '10px', padding: '1.25rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Stars rating={review.rating} />
                  <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                    {review.serviceName}{review.carLabel ? ` · ${review.carLabel}` : ''}
                  </span>
                </div>
                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{timeAgo(review.createdAt)}</span>
              </div>

              {review.comment && (
                <p style={{ fontSize: '0.9375rem', color: '#374151', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
                  {review.comment}
                </p>
              )}

              {review.photoUrls && review.photoUrls.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                  {review.photoUrls.map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt="Review photo"
                      style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }}
                    />
                  ))}
                </div>
              )}

              {review.garageResponse ? (
                <div style={{
                  background: '#f8fafc', border: '1px solid #e5e7eb',
                  borderRadius: '6px', padding: '0.75rem', marginTop: '0.5rem',
                }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', margin: '0 0 0.25rem' }}>Your reply</p>
                  <p style={{ fontSize: '0.875rem', color: '#374151', margin: 0 }}>{review.garageResponse}</p>
                </div>
              ) : replyingTo === review.id ? (
                <div style={{ marginTop: '0.75rem' }}>
                  <textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    placeholder="Write a reply to this review…"
                    rows={3}
                    style={{
                      width: '100%', padding: '0.625rem', borderRadius: '6px',
                      border: '1px solid #d1d5db', fontSize: '0.875rem',
                      resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
                    }}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button
                      onClick={() => submitReply(review.id)}
                      disabled={submitting || !replyText.trim()}
                      style={{
                        padding: '0.4rem 1rem', borderRadius: '6px', border: 'none',
                        background: '#4f46e5', color: 'white', fontWeight: 600,
                        fontSize: '0.8125rem', cursor: submitting ? 'not-allowed' : 'pointer',
                        opacity: submitting || !replyText.trim() ? 0.6 : 1,
                      }}
                    >
                      {submitting ? 'Submitting…' : 'Submit reply'}
                    </button>
                    <button
                      onClick={() => { setReplyingTo(null); setReplyText(''); }}
                      style={{
                        padding: '0.4rem 1rem', borderRadius: '6px',
                        border: '1px solid #d1d5db', background: 'white',
                        color: '#374151', fontSize: '0.8125rem', cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => { setReplyingTo(review.id); setReplyText(''); }}
                  style={{
                    marginTop: '0.5rem', padding: '0.35rem 0.875rem', borderRadius: '6px',
                    border: '1px solid #d1d5db', background: 'white',
                    color: '#374151', fontSize: '0.8125rem', cursor: 'pointer', fontWeight: 500,
                  }}
                >
                  Reply
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
          <button
            onClick={() => setPage(p => p - 1)}
            disabled={page === 0}
            style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: page === 0 ? 'not-allowed' : 'pointer' }}
          >
            ‹
          </button>
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            {page + 1} / {totalPages} · {totalElements} review{totalElements !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages - 1}
            style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer' }}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
